import { useState } from 'react';

// The hover readout shared by the cards on /analysis.
//
// It exists because there were about to be three copies of it. The donut card
// grew one first; the classification bars grew a second when כולל מע״מ arrived and
// there was suddenly a figure the row did not print; the budget bars needed the
// same thing again. Three hand-maintained copies of one readout is how two
// cards on one screen end up formatting the same fact differently — which is
// exactly what this page has spent several passes undoing.
//
// The donut's own tooltip deliberately stays where it is: it follows the
// pointer across an arc rather than anchoring to a row, and it is wired into
// the memo()/useCallback arrangement that keeps recharts from reconciling on
// every mousemove. Sharing this would mean re-opening that.

const TIP_H = 88;

/**
 * Hover state for a list of rows inside one positioned container.
 *
 * `show(i, el)` anchors to the ROW, not the pointer: these rows are ~25px tall
 * and stacked, so a pointer-following tip jitters between neighbours on the way
 * across. The tip sits above the row and flips below for the top ones, which
 * have no room — and the top row is the one most people reach for first, being
 * the biggest. Without the flip it floated up over the card's own title.
 */
export function useHoverTip(containerRef, { tipHeight = TIP_H } = {}) {
  const [tip, setTip] = useState(null);

  const show = (i, el) => {
    const wr = containerRef.current?.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const topWithin = er.top - (wr?.top || 0);
    const below = topWithin < tipHeight;
    setTip({
      i,
      x: er.left - (wr?.left || 0) + er.width / 2,
      y: below ? topWithin + er.height : topWithin,
      below,
    });
  };
  const hide = () => setTip(null);

  return { tip, show, hide };
}

/**
 * `rows` is [{ label, value }] — label muted at the start, value at the end.
 * `title` is the thing being described; `titleNote` is a quiet qualifier after
 * it (a count, a status word).
 *
 * `footnote` is one plain sentence under the figures, for a card whose rows are
 * a RATIO rather than a magnitude — "עלות מזון מהווה 30% מההכנסות" says in words
 * what a label/value pair leaves the reader to assemble. It wraps (the figure
 * rows above stay nowrap), because a sentence broken across lines is normal and
 * a label/value pair broken across lines is not.
 */
export default function ChartTooltip({ tip, title, titleNote, rows, footnote }) {
  if (!tip) return null;
  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute', left: tip.x, top: tip.y,
        transform: tip.below
          ? 'translate(-50%, 10px)'
          : 'translate(-50%, calc(-100% - 10px))',
        background: '#1a1a2e', color: '#fff', borderRadius: 8, padding: '8px 11px',
        fontSize: 12.5, lineHeight: 1.5, pointerEvents: 'none', zIndex: 30,
        whiteSpace: 'nowrap', boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
        // The tip anchors to the ROW centre, and rows span the card, so it is
        // always centred and can only escape the card by being WIDER than it.
        // That was unreachable while these cards were 1288px; at 556px in a
        // two-column layout a long enough name would reach it. The cap plus a
        // wrapping title makes it structurally impossible — the figure rows
        // below stay nowrap, since a label/value pair broken across lines is
        // harder to read than a title on two.
        maxWidth: 260,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4, whiteSpace: 'normal' }}>
        {title}
        {titleNote && <span style={{ fontWeight: 400, color: '#cbd5e1' }}> · {titleNote}</span>}
      </div>
      {rows.map(r => (
        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ color: '#cbd5e1' }}>{r.label}</span>
          <span>{r.value}</span>
        </div>
      ))}
      {footnote && (
        <div style={{
          whiteSpace: 'normal', color: '#cbd5e1', marginTop: 6,
          paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.15)',
        }}>{footnote}</div>
      )}
    </div>
  );
}
